import { useEffect, useState } from 'react'
import { Loader2, RotateCcw } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { toast } from '../../lib/shadcn/sonner'
import { useGetTierReviewSettings, useUpdateTierReviewSettings } from '../../hooks/backend/tierReview'
import { HelpText } from '../../components/HelpText'
import type { TierReviewSettings } from '../../utils/tierReview'

interface SettingsData {
  settings: TierReviewSettings
  defaults: TierReviewSettings
  updated_at: string | null
  updated_by: string | null
}

type TierKey = 'core' | 'limited'
type Field = 'min_units' | 'min_orders' | 'min_retailers'

const ROWS: { key: TierKey; label: string; note: string }[] = [
  { key: 'core', label: 'Core', note: 'Steady sellers across many retailers.' },
  { key: 'limited', label: 'Limited', note: 'Below Core but still selling regularly.' },
]
const FIELDS: { key: Field; label: string }[] = [
  { key: 'min_units', label: 'Units' },
  { key: 'min_orders', label: 'Orders' },
  { key: 'min_retailers', label: 'Retailers' },
]

// Settings > Products > Tier review: the minimums a flavor needs in a quarter
// for each wholesale tier. Below Limited (or Seasonal) it's Special Order.
export default function TierReviewSettingsCard() {
  const getSettings = useGetTierReviewSettings()
  const updateSettings = useUpdateTierReviewSettings()
  const data = getSettings.data as SettingsData | null | undefined
  const [draft, setDraft] = useState<TierReviewSettings | null>(null)

  useEffect(() => {
    void getSettings.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data) setDraft(data.settings)
  }, [data])

  const setTier = (tier: TierKey, field: Field | 'max_top_retailer_pct', value: string) =>
    setDraft(d => (d ? { ...d, [tier]: { ...d[tier], [field]: value === '' ? 0 : Number(value) } } : d))

  const save = async () => {
    if (!draft) return
    try {
      await updateSettings.trigger({ settings: draft }).result
      toast.success('Tier review thresholds saved. They apply to the next review run.')
      void getSettings.trigger({}, { skipCache: true })
    } catch (e) {
      toast.error(`Couldn't save: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  return (
    <Card id="tier-review-settings">
      <CardHeader>
        <CardTitle>Tier Review Thresholds</CardTitle>
        <CardDescription><HelpText>
          What a flavor needs in a quarter (Small + Large bags together) to be suggested for each
          wholesale tier in the quarterly tier review on Products &gt; Tier review. A flavor must meet
          all three minimums; anything below Limited is suggested for Special Order. Seasonal
          flavors and seasonings aren&apos;t reviewed. Changes apply the next time a review runs.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {!draft ? (
          getSettings.error ? (
            <p className="text-sm text-destructive">Couldn&apos;t load the thresholds.</p>
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )
        ) : (
          <>
            <div className="divide-y rounded-md border">
              {ROWS.map(row => (
                <div key={row.key} className="grid grid-cols-1 gap-3 px-3 py-3 sm:grid-cols-[9rem,1fr] sm:items-start">
                  <div>
                    <div className="font-medium">{row.label}</div>
                    <p className="text-xs text-muted-foreground">{row.note}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {FIELDS.map(f => (
                      <div key={f.key} className="space-y-1.5">
                        <Label htmlFor={`tr-${row.key}-${f.key}`} className="text-xs text-muted-foreground">
                          {f.label}
                        </Label>
                        <Input
                          id={`tr-${row.key}-${f.key}`}
                          type="number"
                          min="0"
                          step="1"
                          value={draft[row.key][f.key]}
                          onChange={e => setTier(row.key, f.key, e.target.value)}
                          className="h-9"
                        />
                      </div>
                    ))}
                    {row.key === 'core' && (
                      <div className="space-y-1.5">
                        <Label htmlFor="tr-core-top" className="text-xs text-muted-foreground">
                          Top retailer max %
                        </Label>
                        <Input
                          id="tr-core-top"
                          type="number"
                          min="1"
                          max="100"
                          step="1"
                          value={draft.core.max_top_retailer_pct}
                          onChange={e => setTier('core', 'max_top_retailer_pct', e.target.value)}
                          className="h-9"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tr-trend">Trend guard (%)</Label>
              <Input
                id="tr-trend"
                type="number"
                min="0"
                step="1"
                value={draft.trend_guard_pct}
                onChange={e => setDraft(d => (d ? { ...d, trend_guard_pct: Number(e.target.value) || 0 } : d))}
                className="h-9 w-32"
              />
              <p className="text-xs text-muted-foreground"><HelpText>
                Units per week up this much vs the previous quarter keeps a flavor from moving down;
                down this much keeps it from moving up and flags it as a slow mover.
              </HelpText></p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {data?.updated_at
                  ? `Last saved ${new Date(data.updated_at).toLocaleString()}${data.updated_by ? ` by ${data.updated_by}` : ''}`
                  : 'Using the default thresholds.'}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => data && setDraft(data.defaults)}
                  disabled={!data}
                  className="gap-1.5"
                >
                  <RotateCcw className="h-4 w-4" />
                  Defaults
                </Button>
                <Button type="button" onClick={() => void save()} disabled={updateSettings.loading} className="gap-1.5">
                  {updateSettings.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
